import { useRef } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@design-system/components/button/button';
import { RepeatingFieldShell } from '../primitives/RepeatingFieldShell';
import { ReviewItem } from './ReviewItem';
import { useSectionOpenState } from '../../lib/useSectionOpenState';
import {
  emptyReview,
  ProgramFormValues,
  REVIEWS_MAX_ITEMS,
} from '../../model/programForm.types';

type ReviewsSectionProps = {
  isSubmitting?: boolean;
};

export const ReviewsSection = ({ isSubmitting }: ReviewsSectionProps) => {
  const {
    control,
    clearErrors,
    formState: { errors, submitCount },
  } = useFormContext<ProgramFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'reviews',
  });
  const errorAnchorRef = useRef<HTMLDivElement>(null);
  const reviewsRootError =
    errors.reviews?.root?.message ?? errors.reviews?.message;
  const { isOpen, setIsOpen } = useSectionOpenState({
    submitCount,
    hasError: !!errors.reviews,
    errorAnchorRef,
  });
  const isFull = fields.length >= REVIEWS_MAX_ITEMS;

  return (
    <Controller
      control={control}
      name="reviewsEnabled"
      render={({ field }) => (
        <RepeatingFieldShell
          title="Reviews"
          subtitle="We recommend adding at least 3 reviews before turning this section visible. Maximum of 11 reviews."
          itemLabel="Review"
          fields={fields}
          enabled={field.value}
          onToggleEnabled={(next) => {
            field.onChange(next);
            if (!next) clearErrors('reviews');
          }}
          onRemove={remove}
          renderItem={(index) => (
            <ReviewItem index={index} isSubmitting={isSubmitting} />
          )}
          isSubmitting={isSubmitting}
          testId="program-form-reviews"
          errorMessage={reviewsRootError}
          errorAnchorRef={errorAnchorRef}
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          footer={
            <Button
              color="primaryTonal"
              leftIcon={faPlus}
              onClick={() => append(emptyReview())}
              disabled={isFull || isSubmitting}
              testId="program-form-reviews-add"
            >
              Add
            </Button>
          }
        />
      )}
    />
  );
};
